/**
 * Gli errori di «My Boards» come la schermata li dice (ADR-0191; QGammon `packages/types/src/boards.ts`, `BOARD_ERRORS`, e le
 * rotte in `apps/qgammon-api/src/boards/routes.ts` e `apps/qgammon-gameserver/src/boards/{capture,analysis,routes}.ts`).
 *
 * 🔴 Si parla per `status` + `data.error`, MAI per la frase: la frase la scrive il server in inglese e cambia, il codice no. Un
 * codice che non conosciamo cade su `generic` (e la riga «ogni valore di `BOARD_ERRORS` ha una chiave» del test lo cattura al
 * prossimo bump del pacchetto).
 * ⚠️ Il `429` e' di QUATTRO specie: tre hanno `{error}` (`boards_full`, `daily_cap`, `global_daily_cap`), il quarto e' il limite
 * al minuto di `@fastify/rate-limit`, che NON porta un codice nostro (il suo corpo ha un `error` generico). Chi non e' uno dei tre
 * e' `status === 429` e basta ⇒ `rate_minute`.
 */

/** Il codice dell'errore a schermo: un `BOARD_ERRORS` del server, oppure uno dei due che il server non nomina. */
export type CodiceErroreBoard =
  | 'not_found'
  | 'validation'
  | 'history_pending'
  | 'history_incomplete'
  | 'ply_out_of_range'
  | 'boards_full'
  | 'daily_cap'
  | 'global_daily_cap'
  | 'invalid_position_id'
  | 'snapshot_mismatch'
  | 'snapshot_invalid'
  | 'source_live'
  | 'position_live'
  | 'terminal'
  | 'analysis_busy'
  | 'analysis_timeout'
  /** `429` senza uno dei tre codici: il limite al minuto. */
  | 'rate_minute'
  /** Rete caduta, risposta che non e' JSON, codice sconosciuto. */
  | 'generic';

/** Cosa fa la schermata dopo l'errore: niente, un «Riprova» a mano, o un ritentativo NOSTRO e limitato. */
export type RiprovaBoard = 'nessuno' | 'manuale' | 'automatico';

export interface ErroreBoard {
  codice: CodiceErroreBoard;
  /** La chiave nel catalogo `backgammon` (`myBoards.error.*`, o `myBoards.analysis.closed.*` per le tre chiusure della stima). */
  chiave: string;
  riprova: RiprovaBoard;
  /** `boards_full` apre My Boards: e' l'unico errore che ha una strada dove andare. */
  apreMyBoards: boolean;
}

const E = (
  codice: CodiceErroreBoard,
  chiave: string,
  riprova: RiprovaBoard,
  apreMyBoards = false,
): ErroreBoard => ({ codice, chiave, riprova, apreMyBoards });

/**
 * La tabella: UNA riga per codice. `generic` e' anche il «Riprova» manuale di rete.
 * Le tre chiusure della stima (`source_live`, `position_live`, `terminal`) non sono errori da mostrare come tali: la scheda le
 * legge come stato `closed` (v. `statoAnalisiDa`), ma hanno comunque la loro frase qui, cosi' una sola tabella le copre tutte.
 */
const PER_CODICE: Record<Exclude<CodiceErroreBoard, 'rate_minute' | 'generic'>, ErroreBoard> = {
  not_found: E('not_found', 'myBoards.error.notFound', 'nessuno'),
  // Il corpo che il client ha mandato non torna col contratto: un difetto nostro, ma l'utente non puo' farci niente di diverso
  // dal riprovare (e la riga non offre un secondo tentativo a ciclo).
  validation: E('validation', 'myBoards.error.validation', 'manuale'),
  invalid_position_id: E('invalid_position_id', 'myBoards.error.notFound', 'nessuno'),
  history_pending: E('history_pending', 'myBoards.error.historyPending', 'automatico'),
  history_incomplete: E('history_incomplete', 'myBoards.error.historyIncomplete', 'nessuno'),
  ply_out_of_range: E('ply_out_of_range', 'myBoards.error.plyOutOfRange', 'nessuno'),
  boards_full: E('boards_full', 'myBoards.error.boardsFull', 'nessuno', true),
  daily_cap: E('daily_cap', 'myBoards.error.dailyCap', 'nessuno'),
  // Non rivela il tetto globale: «adesso non disponibile», niente numeri.
  global_daily_cap: E('global_daily_cap', 'myBoards.error.globalCap', 'nessuno'),
  snapshot_mismatch: E('snapshot_mismatch', 'myBoards.error.serverDefect', 'nessuno'),
  snapshot_invalid: E('snapshot_invalid', 'myBoards.error.serverDefect', 'nessuno'),
  source_live: E('source_live', 'myBoards.analysis.closed.sourceLive', 'nessuno'),
  position_live: E('position_live', 'myBoards.analysis.closed.positionLive', 'nessuno'),
  terminal: E('terminal', 'myBoards.analysis.closed.terminal', 'nessuno'),
  // La stima: 503 (coda piena, o oltre il tempo). «Non disponibile ora» + Riprova a mano: nessun ciclo.
  analysis_busy: E('analysis_busy', 'myBoards.error.analysisBusy', 'manuale'),
  analysis_timeout: E('analysis_timeout', 'myBoards.error.analysisBusy', 'manuale'),
};

const GENERICO = E('generic', 'myBoards.error.generic', 'manuale');
const AL_MINUTO = E('rate_minute', 'myBoards.error.rateMinute', 'nessuno');

/** I tre `429` che il server nomina: tutto il resto del `429` e' il limite al minuto. */
const CODICI_429 = new Set<string>(['boards_full', 'daily_cap', 'global_daily_cap']);

/** `{status, data}` di un errore RTK Query (`FetchBaseQueryError`), letto senza fidarsi della forma. */
function leggi(errore: unknown): { status: unknown; codice: string | null } {
  if (typeof errore !== 'object' || errore === null) return { status: undefined, codice: null };
  const e = errore as { status?: unknown; data?: unknown };
  const d = e.data;
  const codice =
    typeof d === 'object' && d !== null && typeof (d as { error?: unknown }).error === 'string'
      ? (d as { error: string }).error
      : null;
  return { status: e.status, codice };
}

export function erroreBoard(errore: unknown): ErroreBoard {
  const { status, codice } = leggi(errore);
  if (status === 429 && (codice === null || !CODICI_429.has(codice))) return AL_MINUTO;
  if (codice !== null && Object.hasOwn(PER_CODICE, codice)) return PER_CODICE[codice as keyof typeof PER_CODICE];
  return GENERICO;
}

/** Tutti i codici che la tabella sa nominare (per il test «ogni `BOARD_ERRORS` ha una voce»). */
export const CODICI_NOMINATI: readonly string[] = Object.keys(PER_CODICE);

/**
 * La stima: i `409`/`422` sono CHIUSURE (stato, non errore), il `503` e la rete sono «non disponibile adesso», un `404` e' «il
 * board non c'e' piu'». Un altro codice e' «non disponibile».
 */
export type EsitoAnalisi =
  | { kind: 'closed'; reason: 'source_live' | 'position_live' | 'terminal' }
  | { kind: 'unavailable' };

export function statoAnalisiDa(errore: unknown): EsitoAnalisi {
  const e = erroreBoard(errore);
  if (e.codice === 'source_live' || e.codice === 'position_live' || e.codice === 'terminal') {
    return { kind: 'closed', reason: e.codice };
  }
  return { kind: 'unavailable' };
}
